"use client";

import React, {
  CSSProperties,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { spotlight_mobile } from "@/generated";
import { detectIOS, parsePx, readSafeAreaBottomPx } from "@/lib/util";
import Image from "next/image";

interface LoaderProps {
  onClick?: () => void;
  spotlight: spotlight_mobile;
  onView?: () => void;
}

const subscribeNoop = () => () => {};

const AdBanner: React.FC<LoaderProps> = (props) => {
  const { onClick, spotlight, onView } = props;
  const isIos = useSyncExternalStore(subscribeNoop, detectIOS, () => false);
  const isClient = useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false,
  );
  const [bottomCorrectionPx, setBottomCorrectionPx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomCorrectionRef = useRef(0);

  useEffect(() => {
    return () => {
      bottomCorrectionRef.current = 0;
      document.documentElement.style.setProperty(
        "--app-ad-banner-fixed-correction",
        "0px",
      );
      document.documentElement.style.setProperty(
        "--app-bottom-banner-height",
        "80px",
      );
    };
  }, []);

  useEffect(() => {
    const rafIds: number[] = [];
    const timeoutIds: number[] = [];

    const updateBannerHeight = () => {
      const el = containerRef.current;
      if (!el) return;
      const height = Math.round(el.getBoundingClientRect().height);
      if (height <= 0) return;
      document.documentElement.style.setProperty(
        "--app-bottom-banner-height",
        `${height}px`,
      );
    };

    const correctBottomPosition = () => {
      if (!isIos) return;
      const el = containerRef.current;
      if (!el) return;
      updateBannerHeight();

      const style = getComputedStyle(document.documentElement);
      const expectedBottom =
        parsePx(style.getPropertyValue("--app-native-tab-bar-height")) +
        readSafeAreaBottomPx();
      const rect = el.getBoundingClientRect();
      const actualBottom = window.innerHeight - rect.bottom;
      const delta = expectedBottom - actualBottom;

      if (Math.abs(delta) <= 1) return;

      const current = bottomCorrectionRef.current;
      const next = Math.round(Math.max(-160, Math.min(160, current + delta)));
      if (Math.abs(next - current) <= 1) return;

      bottomCorrectionRef.current = next;
      document.documentElement.style.setProperty(
        "--app-ad-banner-fixed-correction",
        `${next}px`,
      );
      setBottomCorrectionPx(next);
    };

    const measureBannerPosition = () => {
      const el = containerRef.current;
      if (!el) return;
      if (!isIos) return;
      void el.offsetHeight;
      updateBannerHeight();
      correctBottomPosition();
    };

    const scheduleBottomCorrection = () => {
      measureBannerPosition();
      rafIds.push(requestAnimationFrame(correctBottomPosition));
      timeoutIds.push(window.setTimeout(correctBottomPosition, 250));
      timeoutIds.push(window.setTimeout(correctBottomPosition, 750));
    };

    const preventFixedElementDrag = (event: TouchEvent) => {
      event.preventDefault();
    };

    // Covers SPA back navigation where the component is freshly rendered but
    // the iOS compositor hasn't promoted the layer yet.
    rafIds.push(
      requestAnimationFrame(() => {
        scheduleBottomCorrection();
      }),
    );

    const onPageShow = () => {
      scheduleBottomCorrection();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        scheduleBottomCorrection();
      }
    };

    updateBannerHeight();
    rafIds.push(
      requestAnimationFrame(() => {
        correctBottomPosition();
        timeoutIds.push(window.setTimeout(correctBottomPosition, 250));
      }),
    );

    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibility);
    containerRef.current?.addEventListener(
      "touchmove",
      preventFixedElementDrag,
      {
        passive: false,
      },
    );

    return () => {
      rafIds.forEach((rafId) => cancelAnimationFrame(rafId));
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibility);
      containerRef.current?.removeEventListener(
        "touchmove",
        preventFixedElementDrag,
      );
    };
  }, [isIos]);

  if (!isClient) return null;

  const fixedStyle: CSSProperties = {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: "var(--app-corrected-ad-banner-bottom-offset)",
    zIndex: 30,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    maxHeight: 80,
    overflow: "hidden",
    ...(isIos ? { transform: "translateZ(0)", willChange: "transform" } : {}),
  };

  return createPortal(
    <div
      ref={containerRef}
      data-ad-banner="true"
      data-bottom-correction={Math.round(bottomCorrectionPx)}
      style={fixedStyle}
      onClick={() => {
        onClick?.();
      }}
    >
      <Image
        src={spotlight.picture_url}
        alt="Spotlight Banner"
        width={375}
        height={80}
        priority
        className="object-cover w-full  max-h-20"
        style={{
          width: "100%",
          height: 80,
          maxHeight: 80,
          objectFit: "cover",
          display: "block",
        }}
        onLoad={() => {
          onView?.();
        }}
        onError={(e) => {
          const img = e.currentTarget as HTMLImageElement;
          console.error("[AdBanner]", "onError", {
            src: img.src,
            currentSrc: img.currentSrc,
          });
        }}
      />
    </div>,
    document.body,
  );
};

export default AdBanner;
